import React, {
  ReactNode,
  FC,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios, {
  InternalAxiosRequestConfig,
  AxiosHeaders,
  isAxiosError,
} from 'axios';
import { useConfig } from '@client/providers/ConfigProvider';
import { useSelectedOrganizationName } from '@client/store/organization';
import { useLogoutIfUnauthorized } from '@client/hooks/use-utils.hooks';
import { SHARED_PROJECT } from '@shared/constants';
import {
  isPublicProjectPathname,
  isReservedProjectRoutePrefix,
} from '@shared/constants/router.constants';
import { useLocation } from 'react-router-dom';
import { getUserOrganizationNames } from '@shared/helpers/user.helpers';
import { useUser } from '@client/hooks/use-user.hooks';
import { GlobalLoader } from '@ui/GlobalLoader';
import {
  forceReloadApp,
  redirectToMaintenance,
} from '@client/store/store.helpers';
import {
  ErrorMessage,
  errorStatusCodes,
} from '@shared/constants/server-errors.constants';
import { getKeys } from '@shared/helpers/object.helpers';
import {
  useObjectMemo,
  useSetObjectValueState,
} from '@client/hooks/use-object.hooks';

type RequestInterceptor =
  | ((request: InternalAxiosRequestConfig) => InternalAxiosRequestConfig)
  | ((
      request: InternalAxiosRequestConfig,
    ) => Promise<InternalAxiosRequestConfig>);

interface AxiosConfigProps {
  disableAuth?: boolean;
  children: ReactNode;
}

// To keep track of which interceptors are active. We want to halt rendering until all necessary interceptors are applied.
type InterceptorState = {
  versionNumber: boolean;
  sessionId: boolean;
  authToken: boolean;
  organization: boolean;
  responseError: boolean;
};
type InterceptorKey = keyof InterceptorState;

const isInterceptorsReady = (interceptorState: InterceptorState) =>
  getKeys(interceptorState).every((key) => interceptorState[key] === true);

const useRequestInterceptor = (
  id: InterceptorKey,
  interceptor: RequestInterceptor | undefined,
  setInterceptorState: (key: InterceptorKey, value: boolean) => void,
) => {
  useEffect(() => {
    if (!interceptor) return;
    const handle = axios.interceptors.request.use(interceptor);
    setInterceptorState(id, true);
    return () => {
      axios.interceptors.request.eject(handle);
      setInterceptorState(id, false);
    };
  }, [interceptor, id, setInterceptorState]);
};

const useResponseErrorInterceptor = (
  setInterceptorState: (key: InterceptorKey, value: boolean) => void,
  errorRedirectInterceptor: (error: unknown) => Promise<unknown>,
) => {
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      errorRedirectInterceptor,
    );
    setInterceptorState('responseError', true);
    return () => {
      axios.interceptors.response.eject(id);
      setInterceptorState('responseError', false);
    };
  }, [setInterceptorState, errorRedirectInterceptor]);
};

/**
 * Reject interceptor that redirects on app-wide error responses:
 * 409 version mismatch → /new-version, 503 maintenance → /maintenance.
 * Always re-rejects so callers still handle the error.
 */
const useErrorRedirectInterceptor = () => {
  const [config] = useConfig();

  return useCallback(
    (error: unknown): Promise<unknown> => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === errorStatusCodes[ErrorMessage.AppVersionMismatch]) {
        forceReloadApp(config.version);
      }
      if (status === errorStatusCodes[ErrorMessage.MaintenanceMode]) {
        redirectToMaintenance();
      }
      return Promise.reject(error);
    },
    [config.version],
  );
};

const useCoreAxiosInterceptors = (
  setInterceptorState: (key: InterceptorKey, value: boolean) => void,
) => {
  const [config] = useConfig();

  useEffect(() => {
    if (config.apiUrl) {
      axios.defaults.baseURL = config.apiUrl;
    }
  }, [config.apiUrl]);

  const sessionIdInterceptor = useCallback<RequestInterceptor>(
    (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      request.headers = new AxiosHeaders({
        ...request.headers,
        SessionId: window.sessionStorage.getItem('id'),
      });
      return request;
    },
    [],
  );

  const versionNumberInterceptor = useCallback<RequestInterceptor>(
    (request) => {
      request.headers = new AxiosHeaders({
        ...request.headers,
        AppVersion: config.version,
      });
      return request;
    },
    [config.version],
  );

  const errorRedirectInterceptor = useErrorRedirectInterceptor();

  useRequestInterceptor(
    'versionNumber',
    versionNumberInterceptor,
    setInterceptorState,
  );
  useRequestInterceptor('sessionId', sessionIdInterceptor, setInterceptorState);
  useResponseErrorInterceptor(setInterceptorState, errorRedirectInterceptor);
};

/**
 * Axios interceptors for /shared and /generation routes (no Auth0Provider).
 * Must not call useAuth0 — Auth0Provider is omitted on public project paths.
 */
const usePublicAxiosInterceptor = (): { isReady: boolean } => {
  const [config] = useConfig();

  useEffect(() => {
    if (config.apiUrl) {
      axios.defaults.baseURL = config.apiUrl;
    }
  }, [config.apiUrl]);

  const sessionIdInterceptor = useCallback<RequestInterceptor>((request) => {
    request.headers = new AxiosHeaders({
      ...request.headers,
      SessionId: window.sessionStorage.getItem('id'),
    });
    return request;
  }, []);

  const versionNumberInterceptor = useCallback<RequestInterceptor>(
    (request) => {
      request.headers = new AxiosHeaders({
        ...request.headers,
        AppVersion: config.version,
      });
      return request;
    },
    [config.version],
  );

  const publicOrganizationInterceptor = useCallback<RequestInterceptor>(
    (request) => {
      request.headers = new AxiosHeaders({
        Organization: SHARED_PROJECT,
        ...request.headers,
      });
      return request;
    },
    [],
  );

  const errorRedirectInterceptor = useErrorRedirectInterceptor();

  // Single layout effect: avoid per-interceptor ready flags that can stay false on public routes.
  useLayoutEffect(() => {
    const requestIds = [
      axios.interceptors.request.use(versionNumberInterceptor),
      axios.interceptors.request.use(sessionIdInterceptor),
      axios.interceptors.request.use(publicOrganizationInterceptor),
    ];
    const responseId = axios.interceptors.response.use(
      (response) => response,
      errorRedirectInterceptor,
    );
    return () => {
      requestIds.forEach((id) => axios.interceptors.request.eject(id));
      axios.interceptors.response.eject(responseId);
    };
  }, [
    config.apiUrl,
    config.version,
    publicOrganizationInterceptor,
    sessionIdInterceptor,
    errorRedirectInterceptor,
    versionNumberInterceptor,
  ]);

  return { isReady: true };
};

/**
 * Axios interceptors for authenticated app routes (wrapped in Auth0Provider).
 */
const useAuthenticatedAxiosInterceptor = (): { isReady: boolean } => {
  const { getAccessTokenSilently, isLoading: isLoadingUser } = useAuth0();
  const user = useUser();
  const logoutIfUnauthorized = useLogoutIfUnauthorized();
  const selectedOrganization = useSelectedOrganizationName();

  const [interceptorState, setInterceptorState] =
    useSetObjectValueState<InterceptorState>({
      versionNumber: false,
      sessionId: false,
      authToken: false,
      organization: false,
      responseError: false,
    });

  const authInterceptor = useMemo<RequestInterceptor | undefined>(() => {
    return async (request) => {
      try {
        request.headers = new AxiosHeaders({
          ...request.headers,
          Authorization: `Bearer ${await getAccessTokenSilently()}`,
        });
        return request;
      } catch (e) {
        logoutIfUnauthorized(e);
        throw e;
      }
    };
  }, [getAccessTokenSilently, logoutIfUnauthorized]);

  const organizationInterceptor = useMemo<
    RequestInterceptor | undefined
  >(() => {
    if (isLoadingUser) return undefined;
    return (request) => {
      if (
        user &&
        selectedOrganization &&
        !getUserOrganizationNames(user).includes(selectedOrganization)
      ) {
        throw new Error('User not a member of selected organization');
      }
      const organizationHeader = isReservedProjectRoutePrefix(
        selectedOrganization,
      )
        ? SHARED_PROJECT
        : selectedOrganization;
      request.headers = new AxiosHeaders({
        Organization: organizationHeader ?? null,
        ...request.headers,
      });
      return request;
    };
  }, [selectedOrganization, user, isLoadingUser]);

  useCoreAxiosInterceptors(setInterceptorState);
  useRequestInterceptor('authToken', authInterceptor, setInterceptorState);
  useRequestInterceptor(
    'organization',
    organizationInterceptor,
    setInterceptorState,
  );

  const isReady = useMemo(
    () => isInterceptorsReady(interceptorState),
    [interceptorState],
  );

  return useObjectMemo({ isReady });
};

const AxiosConfigPublic: FC<{ children: ReactNode }> = ({ children }) => {
  const { isReady } = usePublicAxiosInterceptor();
  if (!isReady) {
    return <GlobalLoader.Register text="Loading Nodon" />;
  }
  return children;
};

const AxiosConfigAuthenticated: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isReady } = useAuthenticatedAxiosInterceptor();
  return !isReady ? <GlobalLoader.Register text="Loading Nodon" /> : children;
};

const AxiosConfig: FC<AxiosConfigProps> = ({ disableAuth, children }) => {
  const { pathname } = useLocation();
  // NODON_PROJECT_PATH also matches /generation/...; always use public axios setup on these URLs.
  const usePublicAxios =
    Boolean(disableAuth) || isPublicProjectPathname(pathname);

  const AxiosConfigWrapper = usePublicAxios
    ? AxiosConfigPublic
    : AxiosConfigAuthenticated;

  return <AxiosConfigWrapper>{children}</AxiosConfigWrapper>;
};

export default AxiosConfig;
